import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingUp, MapPin, Database, ChevronLeft, ChevronRight, List } from "lucide-react";
import { Link } from "react-router-dom";

interface WorstOffender {
  hospital_id: string;
  avg_gross_markup_percentage: number;
  expensive_procedures_count: number;
  outlier_expensive_count: number;
  total_procedures: number;
  outlier_percentage: number;
  shame_score_calculated: number;
  hospital_name: string;
  hospital_city?: string;
  hospital_state?: string;
}

export const WorstOffendersSection = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const hospitalsPerPage = 6;
  // Query to get total hospital count
  const { data: totalHospitals } = useQuery({
    queryKey: ['total-hospitals-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('hospitals')
        .select('*', { count: 'exact', head: true });
      if (error) throw error;
      return count || 0;
    }
  });

  // Query to get worst offenders with analytics data
  const { data: worstOffenders, isLoading } = useQuery({
    queryKey: ['worst-offenders-homepage'],
    queryFn: async () => {
      // First get the ranking summary
      const { data: rankings, error: rankingError } = await supabase
        .from('hospital_ranking_summary')
        .select('*')
        .not('avg_gross_markup_percentage', 'is', null)
        // Rank order = median-baseline markup (matches /hospital-rankings #1-18)
        .order('median_gross_markup_percentage', { ascending: false, nullsFirst: false })
        .limit(18);

      if (rankingError) throw rankingError;
      if (!rankings || rankings.length === 0) return [];
      
      // Get hospital details separately
      const hospitalIds = rankings.map(r => r.hospital_id);
      const { data: hospitals, error: hospitalError } = await supabase
        .from('hospitals')
        .select('id, name, city, state')
        .in('id', hospitalIds);
        
      if (hospitalError) throw hospitalError;
      
      // Merge the data
      return rankings.map(ranking => {
        const hospital = hospitals?.find(h => h.id === ranking.hospital_id);
        const totalProcedures = ranking.total_procedures || 0;
        const outlierExpensiveCount = ranking.outlier_expensive_count || 0;
        const outlierPercentage = totalProcedures > 0 ? (outlierExpensiveCount / totalProcedures) * 100 : 0;

        return {
          hospital_id: ranking.hospital_id,
          avg_gross_markup_percentage: ranking.avg_gross_markup_percentage,
          median_gross_markup_percentage: ranking.median_gross_markup_percentage,
          expensive_procedures_count: ranking.expensive_procedures_count,
          outlier_expensive_count: ranking.outlier_expensive_count,
          total_procedures: totalProcedures,
          outlier_percentage: outlierPercentage,
          shame_score_calculated: ranking.shame_score_calculated,
          hospital_name: hospital?.name || 'Unknown Hospital',
          hospital_city: hospital?.city,
          hospital_state: hospital?.state,
        };
      }) as WorstOffender[];
    }
  });

  const getOutlierLevel = (percentage: number) => {
    if (percentage >= 40) return { level: "EXTREME", color: "bg-destructive", textColor: "text-destructive" };
    if (percentage >= 25) return { level: "SEVERE", color: "bg-orange-500", textColor: "text-orange-400" };
    if (percentage >= 10) return { level: "HIGH", color: "bg-yellow-500", textColor: "text-yellow-400" };
    return { level: "MODERATE", color: "bg-muted", textColor: "text-muted-foreground" };
  };

  if (isLoading) {
    return (
      <section id="worst-offenders" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-3 sm:mb-4 glitch" data-text="Today's Worst Offenders">
              Today's Worst Offenders
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              Loading real analytics data...
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-muted/20 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded mb-4 w-2/3"></div>
                  <div className="h-8 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!worstOffenders || worstOffenders.length === 0) {
    return (
      <section id="worst-offenders" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-3 sm:mb-4 glitch" data-text="Today's Worst Offenders">
              Today's Worst Offenders
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              No analytics data available. Run the analytics refresh first.
            </p>
          </div>
          
          <Card className="max-w-2xl mx-auto bg-muted/10 border-destructive/20">
            <CardContent className="p-8 text-center">
              <Database className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Analytics Data Needed</h3>
              <p className="text-muted-foreground mb-4">
                The pricing analytics haven't been calculated yet. Visit the admin panel to refresh the data.
              </p>
              <Link to="/admin" className="inline-flex items-center text-primary hover:text-primary/80 font-medium">
                Go to Admin Panel
                <TrendingUp className="w-4 h-4 ml-1" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="worst-offenders" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 z-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-3 sm:mb-4 glitch" data-text="Today's Worst Offenders">
            Today's Worst Offenders
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
            Hospitals with the most egregious price markups compared to national rates
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild variant="outline" className="bg-background/80 border-destructive/20 hover:bg-destructive/10">
              <Link to="/hospital-rankings" className="flex items-center gap-2">
                <List className="w-4 h-4" />
                View Full Rankings Table
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Pagination Controls */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Page</span>
            <span className="font-mono font-bold">{currentPage}</span>
            <span>of</span>
            <span className="font-mono font-bold">{Math.ceil((worstOffenders?.length || 0) / hospitalsPerPage)}</span>
          </div>
          
          <Button
            variant="outline"
            onClick={() => setCurrentPage(Math.min(Math.ceil((worstOffenders?.length || 0) / hospitalsPerPage), currentPage + 1))}
            disabled={currentPage >= Math.ceil((worstOffenders?.length || 0) / hospitalsPerPage)}
            className="flex items-center gap-2"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {worstOffenders
            .slice((currentPage - 1) * hospitalsPerPage, currentPage * hospitalsPerPage)
            .map((hospital, index) => {
            const actualIndex = (currentPage - 1) * hospitalsPerPage + index;
            const outlierLevel = getOutlierLevel(hospital.outlier_percentage || 0);

            return (
              <Link key={hospital.hospital_id} to={`/hospital/${hospital.hospital_id}`} className="block group">
                <Card className="bg-black/80 border-destructive/20 hover:border-destructive/40 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-destructive/20">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                          {actualIndex + 1}
                        </Badge>
                        <Badge className={`${outlierLevel.color} text-black font-bold px-3 py-1`}>
                          {outlierLevel.level}
                        </Badge>
                      </div>
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                    </div>
                    
                    <CardTitle className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {hospital.hospital_name}
                    </CardTitle>
                    
                    {hospital.hospital_city && hospital.hospital_state && (
                      <div className="flex items-center text-muted-foreground">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="font-mono text-sm">{hospital.hospital_city}, {hospital.hospital_state}</span>
                      </div>
                    )}
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-destructive/10 rounded border border-destructive/20">
                      <span className="text-sm text-muted-foreground">Markup vs National</span>
                      <span className="font-mono text-xl font-bold text-destructive">
                        +{((hospital as any).median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage)?.toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-secondary/50 rounded border border-border">
                      <span className="text-sm text-muted-foreground">Outlier %</span>
                      <span className={`font-mono text-lg font-bold ${outlierLevel.textColor}`}>
                        {hospital.outlier_percentage?.toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="flex justify-between">
                        <span>Expensive procedures:</span>
                        <span className="font-mono">{hospital.expensive_procedures_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Price outliers:</span>
                        <span className="font-mono text-destructive">{hospital.outlier_expensive_count}</span>
                      </div>
                    </div>
                    
                    <div className="pt-2 border-t border-border/50">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">View Details</span>
                        <TrendingUp className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};